import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Box, IconButton, Stack, styled, Typography } from "@mui/material";
import i18next from "i18next";

import { Spacing } from "Infrastructure/Themes/Themes";
import type { UserNotificationDto } from "Notifications/Api/notificationApi";
import { formatRelativeTime } from "Notifications/Utils/formatRelativeTime";
import { notificationIconMap } from "Notifications/Utils/notificationIconMap";

interface NotificationItemProps {
  notification: UserNotificationDto;
  onPress: (notification: UserNotificationDto) => void;
  onDismiss: (userNotificationID: string) => void;
}

function renderNotificationText(notification: UserNotificationDto): string {
  const params = notification.params ? JSON.parse(notification.params) : {};
  return i18next.t(`Notifications:${notification.type}`, params) as string;
}

export const NotificationItem = ({
  notification,
  onPress,
  onDismiss,
}: NotificationItemProps): React.ReactNode => {
  const isUnread = notification.readAt === null;
  const IconComponent =
    notificationIconMap[notification.type] ?? NotificationsIcon;

  return (
    <ItemContainer $isUnread={isUnread} onClick={() => onPress(notification)}>
      <Box sx={{ flexShrink: 0 }}>
        <IconComponent sx={{ color: "text.secondary", fontSize: 22 }} />
      </Box>
      <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isUnread ? 600 : 400,
            color: isUnread ? "text.primary" : "text.secondary",
            wordBreak: "break-word",
          }}
        >
          {renderNotificationText(notification)}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {formatRelativeTime(notification.dateCreated)}
        </Typography>
      </Stack>
      <IconButton
        size="small"
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(notification.userNotificationID);
        }}
        sx={{ flexShrink: 0, color: "text.disabled" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </ItemContainer>
  );
};

const ItemContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isUnread",
})<{ $isUnread: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(Spacing.S)};
  padding: ${(props) => props.theme.spacing(Spacing.M, Spacing.M)};
  border-left: 3px solid
    ${(props) =>
      props.$isUnread ? props.theme.palette.primary.main : "transparent"};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${(props) => props.theme.palette.action.hover};
  }
`;
